import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { Button, Tooltip, CircularProgress } from '@mui/material';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { fetchAdminUsers } from "../api/adminViewApi";
import '../TournamentList.css';

// one line, cut with an ellipsis when it does not fit
const ELLIPSIS_STYLE = {
    display: 'block',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
};

// truncated cell content with the full value on hover
const EllipsisCell = ({ title, children }) => (
    <Tooltip title={title || ''}>
        <span style={ELLIPSIS_STYLE}>{children}</span>
    </Tooltip>
);

const PAGE_SIZE = 10;
const TABLE_COLUMNS = 7;
// keeps the table the same height as a full 10-row page while empty/loading
const ROW_HEIGHT = 44;
const EMPTY_TABLE_HEIGHT = PAGE_SIZE * ROW_HEIGHT;

const AdminViewUsers = () => {
    const [users, setUsers] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    // starts true so the first paint is the loader, never an empty table
    const [loading, setLoading] = useState(true);
    const [jumpPage, setJumpPage] = useState('');
    const [pageError, setPageError] = useState('');

    const navigate = useNavigate();

    const getUsers = async (page = 1) => {
        setLoading(true);
        try {
            const data = await fetchAdminUsers(page, PAGE_SIZE);
            console.log(data, "data");

            setUsers(data?.data?.data || []);
            setTotalPages(data?.data?.totalPages || 1);
            setCurrentPage(data?.data?.currentPage || page);
        } catch (error) {
            console.error('Error fetching users:', error);
            // fall through to the No Data state instead of stale rows
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getUsers(currentPage);
    }, [currentPage]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPageError('');
            if (newPage === currentPage) {
                getUsers(newPage);
            } else {
                setCurrentPage(newPage);
            }
        }
    };

    const handleJumpSubmit = (e) => {
        if (e) {
            e.preventDefault();
        }
        if (loading) {
            return;
        }

        const rawVal = jumpPage.trim();
        if (!rawVal) {
            const msg = totalPages <= 1
                ? "Please enter a valid page number."
                : `Please enter a page number between 1 and ${totalPages}.`;
            setPageError(msg);
            toast.error(msg);
            return;
        }

        const pageNum = Number(rawVal);
        if (isNaN(pageNum) || !Number.isInteger(pageNum) || pageNum < 1 || pageNum > totalPages) {
            const msg = totalPages <= 1
                ? "Page number must be 1."
                : `Please enter a page number between 1 and ${totalPages}.`;
            setPageError(msg);
            toast.error(msg);
            return;
        }

        setPageError('');
        handlePageChange(pageNum);
        setJumpPage('');
    };

    const handleBackClick = () => {
        navigate('/dashboard');
    };

    // 24-hour clock, no AM/PM
    const formatDate = (dateString) => {
        if (!dateString) {
            return 'NA';
        }

        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return 'NA';
        }

        return date.toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
        }).replace(',', '');
    };

    const downloadExcel = () => {
        const rows = users.map(userTeam => ({
            "User ID": userTeam.users?.id || 'N/A',
            "Mobile": userTeam.users?.mobile || 'N/A',
            "Username": userTeam.users?.username || 'N/A',
            "Team Name": userTeam.team?.name || 'N/A',
            "Trainer Name": userTeam.admin?.name || 'N/A',
            "Trainer Email": userTeam.admin?.email || 'N/A',
            "Points": userTeam.users?.points ?? 0,
            "Added On": formatDate(userTeam.createdAt)
        }));

        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Users");

        XLSX.writeFile(wb, "admin_users.xlsx");
    };

    return (
        <div style={{ padding: '20px' }}>
            <h1>Users Under My Trainers</h1>
            <Button
                variant="contained"
                color="secondary"
                onClick={handleBackClick}
                sx={{ marginBottom: 2 }}
            >
                Back to Dashboard
            </Button>
            <Button
                variant="contained"
                color="primary"
                onClick={downloadExcel}
                sx={{ marginLeft: 2, marginBottom: 2 }}
            >
                Download Excel
            </Button>

            {/* the table is always rendered so the layout stays identical
                between the loading, empty and populated states */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px', tableLayout: 'fixed' }}>
                <colgroup>
                    <col style={{ width: '90px' }} />
                    <col style={{ width: '130px' }} />
                    <col style={{ width: '180px' }} />
                    <col style={{ width: '180px' }} />
                    <col style={{ width: '240px' }} />
                    <col style={{ width: '90px' }} />
                    <col style={{ width: '160px' }} />
                </colgroup>
                <thead>
                    <tr>
                        <th>User ID</th>
                        <th>Mobile</th>
                        <th>Username</th>
                        <th>Team Name</th>
                        <th>Trainer</th>
                        <th>Points</th>
                        <th>Added On</th>
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        // Loader stays until the response lands
                        <tr>
                            <td
                                colSpan={TABLE_COLUMNS}
                                style={{
                                    height: EMPTY_TABLE_HEIGHT,
                                    textAlign: 'center',
                                    verticalAlign: 'middle',
                                }}
                            >
                                <CircularProgress size={40} />
                            </td>
                        </tr>
                    ) : users.length === 0 ? (
                        <tr>
                            <td
                                colSpan={TABLE_COLUMNS}
                                style={{
                                    height: EMPTY_TABLE_HEIGHT,
                                    textAlign: 'center',
                                    verticalAlign: 'middle',
                                    fontWeight: 500,
                                    color: '#666',
                                }}
                            >
                                No Data Available
                            </td>
                        </tr>
                    ) : (
                        users.map((userTeam, index) => {
                            const userId = userTeam.users?.id || 'N/A';
                            const mobile = userTeam.users?.mobile || 'N/A';
                            const username = userTeam.users?.username || 'N/A';
                            const teamName = userTeam.team?.name || 'N/A';
                            const trainerName = userTeam.admin?.name || '';
                            const trainerEmail = userTeam.admin?.email || '';
                            let trainerTitle = 'N/A';
                            if (trainerName && trainerEmail) {
                                trainerTitle = `${trainerName} (${trainerEmail})`;
                            } else if (trainerName) {
                                trainerTitle = trainerName;
                            } else if (trainerEmail) {
                                trainerTitle = trainerEmail;
                            }

                            return (
                                <tr key={index}>
                                    <td>{userId}</td>
                                    <td>{mobile}</td>
                                    <td>
                                        <EllipsisCell title={username}>
                                            {username}
                                        </EllipsisCell>
                                    </td>
                                    <td>
                                        <EllipsisCell title={teamName}>
                                            {teamName}
                                        </EllipsisCell>
                                    </td>
                                    <td>
                                        {/* name and email on one line */}
                                        <EllipsisCell title={trainerTitle}>
                                            {trainerName ? <strong>{trainerName}</strong> : null}
                                            {trainerEmail ? (
                                                <span style={{ fontSize: '12px', color: '#666' }}>
                                                    {trainerName ? ` (${trainerEmail})` : trainerEmail}
                                                </span>
                                            ) : null}
                                            {!trainerName && !trainerEmail ? 'N/A' : null}
                                        </EllipsisCell>
                                    </td>
                                    <td>{userTeam.users?.points ?? 0}</td>
                                    <td>{formatDate(userTeam.createdAt)}</td>
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>

            {/* hidden while loading and when there is nothing to page through */}
            {!loading && users.length > 0 && (
                <div style={{ marginTop: '20px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                    <button onClick={() => handlePageChange(currentPage - 1)} disabled={loading || currentPage === 1}>
                        Previous
                    </button>
                    <span style={{ margin: '0 10px' }}> Page {currentPage} of {totalPages} </span>
                    <button onClick={() => handlePageChange(currentPage + 1)} disabled={loading || currentPage === totalPages}>
                        Next
                    </button>

                    <form
                        onSubmit={handleJumpSubmit}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginLeft: '15px' }}
                    >
                        <label htmlFor="jump-page-input" style={{ fontSize: '14px', color: '#444' }}>
                            Jump to page:
                        </label>
                        <input
                            id="jump-page-input"
                            type="number"
                            min="1"
                            max={totalPages}
                            value={jumpPage}
                            onChange={(e) => {
                                setJumpPage(e.target.value);
                                if (pageError) setPageError('');
                            }}
                            placeholder="Page #"
                            disabled={loading}
                            style={{
                                width: '70px',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                border: pageError ? '1px solid #d32f2f' : '1px solid #ccc',
                                fontSize: '14px',
                                outline: 'none',
                            }}
                        />
                        <button
                            type="submit"
                            disabled={loading}
                            style={{
                                padding: '4px 12px',
                                cursor: loading ? 'not-allowed' : 'pointer',
                            }}
                        >
                            Go
                        </button>
                    </form>

                    {pageError && (
                        <span style={{ color: '#d32f2f', fontSize: '13px', marginLeft: '5px' }}>
                            {pageError}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

export default AdminViewUsers;


